import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';
import { useTenant } from './contexts/TenantContext';

function Reminders() {
  var { tenant } = useTenant();
  var [settings, setSettings] = useState(null);
  var [log, setLog] = useState([]);
  var [tab, setTab] = useState('settings');
  var [testEmail, setTestEmail] = useState('');
  var [saving, setSaving] = useState(false);
  var [testSending, setTestSending] = useState(false);
  var [msg, setMsg] = useState(null);
  var [loadError, setLoadError] = useState(null);

  var loadSettings = useCallback(async function() {
    try {
      var res = await axios.get('/api/reminders/settings');
      setSettings(res.data);
      setLoadError(null);
    } catch (e) {
      console.error(e);
      setLoadError(e.response?.data?.error || e.message || 'Failed to load reminder settings');
    }
  }, []);

  var loadLog = useCallback(async function() {
    try {
      var res = await axios.get('/api/reminders/log');
      setLog(res.data);
    } catch (e) {
      console.error(e);
      setLog([]);
    }
  }, []);

  useEffect(function() { loadSettings(); loadLog(); }, [loadSettings, loadLog]);

  var saveSettings = async function() {
    setSaving(true);
    try {
      var res = await axios.put('/api/reminders/settings', settings);
      if (res.data) { setMsg({ type: 'success', text: 'Settings saved!' }); }
    } catch (e) { setMsg({ type: 'error', text: 'Failed to save' }); }
    setSaving(false);
    setTimeout(function() { setMsg(null); }, 3000);
  };

  var sendTest = async function() {
    if (!testEmail) return;
    setTestSending(true);
    try {
      var res = await axios.post('/api/reminders/send-test', { email: testEmail, type: 'email' });
      var data = res.data;
      setMsg({ type: data.success ? 'success' : 'error', text: data.success ? 'Test email sent! Check your inbox.' : (data.message || 'Failed to send') });
      loadLog();
    } catch (e) { setMsg({ type: 'error', text: 'Error sending test' }); }
    setTestSending(false);
    setTimeout(function() { setMsg(null); }, 4000);
  };

  var triggerCheck = async function() {
    try {
      var res = await axios.post('/api/reminders/check');
      var data = res.data;
      setMsg({ type: 'success', text: 'Check complete: ' + data.emailsSent + ' emails, ' + data.smsLogged + ' SMS logged' });
      loadLog();
    } catch (e) { setMsg({ type: 'error', text: 'Error running check' }); }
    setTimeout(function() { setMsg(null); }, 4000);
  };

  if (!settings && !loadError) return (
    <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading reminder settings...</div>
  );

  if (loadError) return (
    <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ background: '#12121f', borderRadius: '16px', padding: '40px', border: '1px solid #1e1e35', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: '700', margin: '0 0 8px' }}>Reminders Setup Required</h2>
        <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px', lineHeight: 1.6 }}>
          The reminder system needs database columns added to your tenants table. Run this migration in your Supabase SQL editor:
        </p>
        <div style={{ background: '#252840', borderRadius: '10px', padding: '16px', textAlign: 'left', fontSize: '12px', color: '#22c55e', fontFamily: 'monospace', marginBottom: '16px', overflowX: 'auto', whiteSpace: 'pre', lineHeight: 1.6 }}>
{`ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  reminder_email_enabled BOOLEAN DEFAULT true;
ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  reminder_sms_enabled BOOLEAN DEFAULT false;
ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  reminder_hours_before INTEGER DEFAULT 24;
ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  reminder_email_template TEXT;
ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  reminder_sms_template TEXT;
ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  notification_method TEXT DEFAULT 'email';
ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  notification_email TEXT;
ALTER TABLE tenants ADD COLUMN IF NOT EXISTS
  notification_phone TEXT;

CREATE TABLE IF NOT EXISTS reminder_log (
  id SERIAL PRIMARY KEY,
  tenant_id INTEGER REFERENCES tenants(id),
  type TEXT DEFAULT 'email',
  status TEXT DEFAULT 'sent',
  recipient TEXT,
  customer_name TEXT,
  service_name TEXT,
  booking_date TIMESTAMPTZ,
  created_at TIMESTAMPTZ DEFAULT NOW()
);`}
        </div>
        <p style={{ color: '#666', fontSize: '12px', margin: '0 0 16px' }}>Error: {loadError}</p>
        <button onClick={function() { setLoadError(null); loadSettings(); loadLog(); }}
          style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    </div>
  );

  var statCards = [
    { label: 'Emails Sent', value: log.filter(function(l) { return l.type === 'email' && l.status === 'sent'; }).length, color: '#00b894' },
    { label: 'SMS Logged', value: log.filter(function(l) { return l.type === 'sms'; }).length, color: '#ffab40' },
    { label: 'Failed', value: log.filter(function(l) { return l.status === 'failed'; }).length, color: '#d63031' },
    { label: 'Total', value: log.length, color: '#6c5ce7' },
  ];

  return (
    <div style={{ padding: '32px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '800', margin: '0 0 6px', letterSpacing: '-0.5px' }}>
          {'\u{1F514}'} Appointment Reminders
        </h1>
        <p style={{ color: '#888', fontSize: '15px', margin: 0 }}>
          Reduce no-shows with automatic email and SMS reminders
        </p>
      </div>

      {/* Toast */}
      {msg && (
        <div style={{ padding: '12px 20px', borderRadius: '10px', marginBottom: '20px', fontSize: '14px', fontWeight: '600',
          background: msg.type === 'success' ? '#00b89420' : '#d6303120',
          color: msg.type === 'success' ? '#00b894' : '#d63031',
          border: '1px solid ' + (msg.type === 'success' ? '#00b89440' : '#d6303140'),
        }}>{msg.text}</div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '28px' }}>
        {statCards.map(function(s, i) {
          return (
            <div key={i} style={{ background: '#12121f', borderRadius: '14px', padding: '20px', border: '1px solid #1e1e35' }}>
              <div style={{ color: s.color, fontSize: '28px', fontWeight: '800' }}>{s.value}</div>
              <div style={{ color: '#888', fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: '#252840', borderRadius: '12px', padding: '4px', marginBottom: '24px' }}>
        {[{ id: 'settings', label: 'Settings' }, { id: 'log', label: 'Activity Log' }].map(function(t) {
          return (
            <button key={t.id} onClick={function() { setTab(t.id); if (t.id === 'log') loadLog(); }} style={{
              flex: 1, padding: '10px', borderRadius: '10px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              background: tab === t.id ? 'linear-gradient(135deg, #ff6b2b, #ffab40)' : 'transparent',
              color: tab === t.id ? '#fff' : '#888',
            }}>{t.label}</button>
          );
        })}
      </div>

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div>
          {/* Email Reminders */}
          <div style={{ background: '#12121f', borderRadius: '16px', padding: '28px', border: '1px solid #1e1e35', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', margin: '0 0 4px' }}>
                  {'\u{2709}\uFE0F'} Email Reminders
                </h3>
                <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>Free &middot; Up to 100/day via Resend</p>
              </div>
              <button onClick={function() { setSettings({...settings, email_enabled: !settings.email_enabled}); }} style={{
                width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                background: settings.email_enabled ? 'linear-gradient(135deg, #ff6b2b, #ffab40)' : '#333',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: '3px', transition: 'left 0.2s',
                  left: settings.email_enabled ? '27px' : '3px',
                }}></div>
              </button>
            </div>

            {settings.email_enabled && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Send reminder</label>
                  <select value={settings.hours_before} onChange={function(e) { setSettings({...settings, hours_before: parseInt(e.target.value)}); }}
                    style={{ width: '100%', padding: '12px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none' }}>
                    <option value={2}>2 hours before</option>
                    <option value={4}>4 hours before</option>
                    <option value={12}>12 hours before</option>
                    <option value={24}>24 hours before (recommended)</option>
                    <option value={48}>48 hours before</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: '#888', display: 'block', marginBottom: '6px' }}>Second Reminder</label>
                  <select value={settings.second_reminder_hours || 0} onChange={function(e) { setSettings({...settings, second_reminder_hours: parseInt(e.target.value)}); }}
                    style={{ width: '100%', padding: '8px 12px', background: '#1a1c2e', border: '1px solid #1e1e3a', borderRadius: '8px', color: '#fff', fontSize: '13px' }}>
                    <option value={0}>None</option>
                    <option value={1}>1 hour before</option>
                    <option value={2}>2 hours before</option>
                    <option value={4}>4 hours before</option>
                  </select>
                </div>


                {/* Test Email */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="email" placeholder="Enter email to test" value={testEmail}
                    onChange={function(e) { setTestEmail(e.target.value); }}
                    style={{ flex: 1, padding: '12px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none' }} />
                  <button onClick={sendTest} disabled={testSending} style={{
                    padding: '12px 20px', background: '#1e1e35', border: '1px solid #2a2a4a', borderRadius: '10px',
                    color: '#ff6b2b', fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>{testSending ? 'Sending...' : 'Send Test'}</button>
                </div>
              </>
            )}
          </div>

          {/* SMS Reminders */}
          <div style={{ background: '#12121f', borderRadius: '16px', padding: '28px', border: '1px solid #1e1e35', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div>
                <h3 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', margin: '0 0 4px' }}>
                  {'\u{1F4F1}'} SMS Reminders
                </h3>
                <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
                  {settings.sms_available ? 'Active' : 'SMS not configured — check Settings'}
                </p>
              </div>
              {settings.sms_available ? (
                <button onClick={function() { setSettings({...settings, sms_enabled: !settings.sms_enabled}); }} style={{
                  width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                  background: settings.sms_enabled ? 'linear-gradient(135deg, #ff6b2b, #ffab40)' : '#333',
                  position: 'relative', transition: 'background 0.2s',
                }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: '3px', transition: 'left 0.2s',
                    left: settings.sms_enabled ? '27px' : '3px',
                  }}></div>
                </button>
              ) : (
                <span style={{ padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', background: '#ffab4020', color: '#ffab40' }}>Upgrade</span>
              )}
            </div>

            {settings.sms_enabled && settings.sms_available && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>SMS Template</label>
                <textarea value={settings.sms_template} onChange={function(e) { setSettings({...settings, sms_template: e.target.value}); }}
                  rows={3} style={{ width: '100%', padding: '12px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                <p style={{ color: '#666', fontSize: '12px', margin: '6px 0 0' }}>
                  Variables: {'{customer_name}'} {'{service_name}'} {'{date}'} {'{time}'} {'{business_name}'}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={saveSettings} disabled={saving} style={{
              padding: '14px 28px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none',
              borderRadius: '12px', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer',
            }}>{saving ? 'Saving...' : 'Save Settings'}</button>
            <button onClick={triggerCheck} style={{
              padding: '14px 28px', background: '#1e1e35', border: '1px solid #2a2a4a',
              borderRadius: '12px', color: '#ccc', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
            }}>Run Reminder Check Now</button>
          </div>

          {/* Notification Contact Settings */}
          <div style={{ background: '#12121f', borderRadius: '16px', padding: '28px', border: '1px solid #1e1e35', marginTop: '16px' }}>
            <h3 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', margin: '0 0 6px' }}>
              {'\u{2709}\uFE0F'} Notification Delivery Settings
            </h3>
            <p style={{ color: '#888', fontSize: '13px', margin: '0 0 20px' }}>
              Choose how your customers receive reminders. Enter the email and/or phone that sends notifications.
            </p>

            {/* Delivery method choice */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Send reminders via</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { value: 'email', label: 'Email Only' },
                  { value: 'sms', label: 'SMS Only' },
                  { value: 'both', label: 'Both' },
                ].map(function(opt) {
                  var isActive = (settings.notification_method || 'email') === opt.value;
                  return (
                    <button key={opt.value} onClick={function() { setSettings({...settings, notification_method: opt.value}); }}
                      style={{
                        flex: 1, padding: '10px 16px', borderRadius: '10px', border: '1px solid',
                        borderColor: isActive ? '#ff6b2b' : '#2a2a4a',
                        background: isActive ? '#ff6b2b20' : '#252840',
                        color: isActive ? '#ff6b2b' : '#888',
                        fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                      }}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notification Email */}
            {(settings.notification_method || 'email') !== 'sms' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Sender / Reply-to Email
                </label>
                <input type="email" placeholder="e.g. appointments@yourbusiness.com"
                  value={settings.notification_email || ''}
                  onChange={function(e) { setSettings({...settings, notification_email: e.target.value}); }}
                  style={{ width: '100%', padding: '12px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                <p style={{ color: '#666', fontSize: '12px', margin: '6px 0 0' }}>
                  Customers will see this as the sender. Replies go here too.
                </p>
              </div>
            )}

            {/* Notification Phone */}
            {(settings.notification_method || 'email') !== 'email' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#aaa', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Business SMS Phone Number
                </label>
                <input type="tel" placeholder="e.g. (702) 555-1234"
                  value={settings.notification_phone || ''}
                  onChange={function(e) { setSettings({...settings, notification_phone: e.target.value}); }}
                  style={{ width: '100%', padding: '12px', background: '#252840', border: '1px solid #2a2a4a', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                <p style={{ color: '#666', fontSize: '12px', margin: '6px 0 0' }}>
                  SMS reminders will be sent from this number (requires Twilio setup).
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Tab */}
      {tab === 'log' && (
        <div style={{ background: '#12121f', borderRadius: '16px', border: '1px solid #1e1e35', overflow: 'hidden' }}>
          {log.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#888' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>{'\u{1F4EC}'}</div>
              <p style={{ fontSize: '15px', margin: '0 0 4px' }}>No reminders sent yet</p>
              <p style={{ fontSize: '13px', color: '#666' }}>Reminders will appear here when they're sent</p>
            </div>
          ) : (
            <div>
              {log.map(function(entry, i) {
                var isEmail = entry.type === 'email';
                var isSent = entry.status === 'sent';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderBottom: i < log.length - 1 ? '1px solid #1e1e35' : 'none' }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
                      background: isEmail ? '#6c5ce715' : '#ffab4015',
                    }}>{isEmail ? '\u{2709}\uFE0F' : '\u{1F4F1}'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                        {entry.customer_name || entry.recipient} {entry.service_name ? ' \u2014 ' + entry.service_name : ''}
                      </div>
                      <div style={{ color: '#888', fontSize: '12px' }}>{entry.recipient}</div>
                    </div>
                    <div style={{
                      padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                      background: isSent ? '#00b89420' : entry.status === 'logged' ? '#ffab4020' : '#d6303120',
                      color: isSent ? '#00b894' : entry.status === 'logged' ? '#ffab40' : '#d63031',
                    }}>{entry.status}</div>
                    <div style={{ color: '#666', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Reminders;
